import React, { useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Progress } from "../components/ui/progress";
import { ShieldWarning, Warning, SealCheck, Fingerprint } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Rules of Engagement consent gate. Mandatory before any exploitation scan.
 * On sign-off it writes an immutable consent record, then launches the scan.
 */
export default function RoEGate({ open, onOpenChange, target, workspaceId, onLaunched }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [scope, setScope] = useState(
    "Authorized active exploitation limited to the target(s) listed below. No lateral movement, data exfiltration, or denial-of-service. Testing confined to the agreed time window."
  );
  const [timeWindow, setTimeWindow] = useState("Business hours (09:00–17:00 AEST), single session");
  const [signName, setSignName] = useState(user?.name || "");
  const [signRole, setSignRole] = useState(user?.role === "admin" ? "Administrator" : "Lead Pentester");
  const [ack, setAck] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setStep(1); setAck(false); setAck2(false); setSubmitting(false); };

  const launch = async () => {
    setSubmitting(true);
    try {
      const consentRes = await api.post("/consent", {
        workspace_id: workspaceId,
        target_id: target.id,
        scope,
        authorized_targets: [target.value],
        time_window: timeWindow,
        signatory_name: signName,
        signatory_role: signRole,
        acknowledged: true,
      });
      const consentId = consentRes.data.id;
      await api.post("/scans", {
        workspace_id: workspaceId,
        target_id: target.id,
        type: "exploitation",
        consent_id: consentId,
      });
      toast.success("RoE signed · exploitation scan queued", { description: `Consent ID ${consentId.slice(0, 8)}` });
      onOpenChange(false);
      reset();
      onLaunched?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="bg-[#0b1220] border-red-500/30 text-white max-w-lg" data-testid="roe-gate-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2 text-red-400">
            <ShieldWarning size={22} weight="fill" />
            <DialogTitle className="text-red-400">Rules of Engagement — Consent Gate</DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-2">
          {[1, 2].map((n) => (
            <div key={n} className={`h-1.5 flex-1 rounded-full ${step >= n ? "bg-red-500" : "bg-slate-700"}`} />
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2.5 flex gap-2 text-sm text-red-200">
              <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
              You are about to authorize <b>active exploitation</b> against a live target. This is destructive
              testing and is legally binding. Proceed only with documented authorization.
            </div>
            <div>
              <Label className="overline text-slate-500">Authorized target</Label>
              <div className="mt-1 rounded-md bg-[#0f172a] border border-[#1e293b] px-3 py-2 font-mono text-blue-300">
                {target?.name} · {target?.value}
              </div>
            </div>
            <div>
              <Label className="overline text-slate-500">Scope</Label>
              <Textarea data-testid="roe-scope-input" value={scope} onChange={(e) => setScope(e.target.value)}
                className="mt-1 bg-[#0f172a] border-[#1e293b] text-sm" rows={4} />
            </div>
            <div>
              <Label className="overline text-slate-500">Permitted time window</Label>
              <Input data-testid="roe-timewindow-input" value={timeWindow} onChange={(e) => setTimeWindow(e.target.value)}
                className="mt-1 bg-[#0f172a] border-[#1e293b]" />
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="roe-ack-scope" checked={ack} onCheckedChange={setAck} className="mt-0.5 border-slate-600" />
              <span className="text-sm text-slate-300">
                I confirm the target above is within the agreed scope and I have written authorization to test it.
              </span>
            </label>
            <Button data-testid="roe-continue-button" disabled={!ack} onClick={() => setStep(2)}
              className="w-full bg-red-600 hover:bg-red-500 text-white disabled:opacity-40">
              Continue to sign-off
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline text-slate-500">Signatory name</Label>
                <Input data-testid="roe-signname-input" value={signName} onChange={(e) => setSignName(e.target.value)}
                  className="mt-1 bg-[#0f172a] border-[#1e293b]" />
              </div>
              <div>
                <Label className="overline text-slate-500">Role</Label>
                <Input data-testid="roe-signrole-input" value={signRole} onChange={(e) => setSignRole(e.target.value)}
                  className="mt-1 bg-[#0f172a] border-[#1e293b]" />
              </div>
            </div>
            <div className="rounded-md bg-[#0f172a] border border-[#1e293b] px-3 py-2.5 text-xs text-slate-400 flex items-center gap-2">
              <Fingerprint size={16} className="text-blue-400" />
              A timestamped, immutable consent record will be written to the audit log at sign-off.
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox data-testid="roe-ack-final" checked={ack2} onCheckedChange={setAck2} className="mt-0.5 border-slate-600" />
              <span className="text-sm text-slate-300">
                I acknowledge the disclaimer, accept responsibility for this exploitation test, and provide my
                sign-off. Scans outside the acknowledged scope will be blocked.
              </span>
            </label>
            <div className="flex gap-3">
              <Button variant="ghost" onClick={() => setStep(1)} className="text-slate-400 hover:text-white hover:bg-slate-800">
                Back
              </Button>
              <Button data-testid="roe-signoff-button" disabled={!ack2 || !signName || submitting} onClick={launch}
                className="flex-1 bg-red-600 hover:bg-red-500 text-white disabled:opacity-40">
                <SealCheck size={18} weight="fill" className="mr-2" />
                {submitting ? "Signing…" : "Sign & Launch Exploitation"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
